<template>
  <div
    class="onboarding-screen"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
  >
    <!-- Hero zone: tinted gradient with floating badges + per-slide feature card -->
    <div class="hero">
      <div class="hero-decor" aria-hidden="true">
        <span class="bubble bubble--1"></span>
        <span class="bubble bubble--2"></span>
        <span class="bubble bubble--3"></span>
      </div>

      <div
        :key="'hero-' + currentSlide"
        :class="['hero-stage', 'anim--' + slideDirection]"
      >
        <!-- Floating badges (decorative, per slide) -->
        <span class="badge badge--tl">
          <i class="badge-dot badge-dot--purple"></i>{{ slide.badge1 }}
        </span>
        <span class="badge badge--tr">
          <i class="badge-dot badge-dot--green"></i>{{ slide.badge2 }}
        </span>
        <span class="badge badge--br">
          <i class="badge-dot badge-dot--orange"></i>{{ slide.badge3 }}
        </span>

        <!-- Slide 0: Personalization — avatar + level/goals/style bars -->
        <div v-if="currentSlide === 0" class="feature-card">
          <div class="avatar">
            <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z"
              />
            </svg>
          </div>
          <div
            v-for="bar in personalizeBarsResolved"
            :key="bar.label"
            class="meter"
          >
            <span class="meter-label">{{ bar.label }}</span>
            <span class="meter-track">
              <span class="meter-fill" :style="{ width: bar.pct }"></span>
            </span>
          </div>
        </div>

        <!-- Slide 1: Course builder — lesson rows + progress ring -->
        <div v-else-if="currentSlide === 1" class="feature-card">
          <div class="course-head">
            <span class="course-title">{{ $t("onboarding.mobileIntro.courseCardTitle") }}</span>
            <span class="course-ring">
              <svg viewBox="0 0 36 36" width="38" height="38" aria-hidden="true">
                <circle class="ring-bg" cx="18" cy="18" r="15.5" />
                <circle
                  class="ring-fg"
                  cx="18"
                  cy="18"
                  r="15.5"
                  stroke-dasharray="97"
                  stroke-dashoffset="58"
                />
              </svg>
              <span class="course-ring-label">40%</span>
            </span>
          </div>
          <div
            v-for="(lesson, i) in courseLessons"
            :key="i"
            :class="['lesson-row', { 'lesson-row--done': lesson.done }]"
          >
            <span class="lesson-mark">
              <svg
                v-if="lesson.done"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                aria-hidden="true"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m5 13 4 4L19 7"
                />
              </svg>
              <i v-else class="lesson-dot"></i>
            </span>
            <span class="lesson-bar" :style="{ width: lesson.width }"></span>
          </div>
        </div>

        <!-- Slide 2: Daily habit — focal minutes + supported-language flags -->
        <div v-else class="feature-card">
          <div class="habit-focal">
            <span class="habit-num">2</span>
            <span class="habit-unit">{{ $t("onboarding.mobileIntro.habitUnit") }}</span>
          </div>
          <div class="flag-strip">
            <img
              v-for="code in flagCodes"
              :key="code"
              class="flag-chip"
              :src="flagSrc(code)"
              :alt="$t('languages.' + code)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Content zone: dots, copy, CTA -->
    <div class="content">
      <div class="progress-dots">
        <span
          v-for="i in totalSlides"
          :key="i"
          :class="['dot', { active: currentSlide === i - 1 }]"
        ></span>
      </div>

      <div
        :key="'text-' + currentSlide"
        :class="['slide-text', 'anim--' + slideDirection]"
      >
        <h1 class="headline">{{ slide.title }}</h1>
        <p class="body">{{ slide.body }}</p>
      </div>

      <div class="cta">
        <button
          v-if="!isLastSlide"
          class="btn-primary"
          @click="advanceSlide"
        >
          {{ $t("common.next") }}
        </button>
        <button
          v-else
          class="btn-primary"
          :disabled="signingIn"
          :class="{ 'btn-primary--disabled': signingIn }"
          @click="signIn"
        >
          {{
            signingIn
              ? $t("onboarding.mobileIntro.signingIn")
              : $t("onboarding.mobileIntro.getLearning")
          }}
        </button>

        <button
          type="button"
          class="signin-link"
          :class="{ 'signin-link--placeholder': isLastSlide }"
          :disabled="signingIn || isLastSlide"
          :aria-hidden="isLastSlide ? 'true' : 'false'"
          :tabindex="isLastSlide ? -1 : 0"
          @click="signIn"
        >
          {{ $t("onboarding.mobileIntro.alreadyHaveAccount") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { flagsStore } from "@/store/countryFlags.js";
import { authUtils } from "@/scripts/authUtils";
import { authSetupMixin } from "@/mixins/authSetupMixin";

export default {
  name: "MobileOnboardingView",
  mixins: [authSetupMixin],
  data() {
    return {
      currentSlide: 0,
      slideDirection: "forward",
      signingIn: false,
      touchStartX: 0,
      touchStartY: 0,
      // Curated ~12-flag representative subset for the slide-3 flag strip — NOT
      // the full supported set (26 base languages would overflow the card on
      // short viewports like iPhone SE). This is decorative; the slide-3 copy's
      // language count is a static translation key (habitBody), not derived from
      // this array, so the two are maintained independently.
      flagCodes: [
        "zh-cn",
        "en-us",
        "es-es",
        "fr-fr",
        "de",
        "it",
        "hi",
        "ja",
        "ko",
        "pt-pt",
        "ru",
        "ta-in",
      ],
      // Static illustration data — purely decorative, no real user data.
      personalizeBars: [
        { labelKey: "onboarding.mobileIntro.labelLevel", pct: "55%" },
        { labelKey: "onboarding.mobileIntro.labelGoals", pct: "80%" },
        { labelKey: "onboarding.mobileIntro.labelStyle", pct: "40%" },
      ],
      courseLessons: [
        { done: true, width: "85%" },
        { done: true, width: "70%" },
        { done: false, width: "78%" },
        { done: false, width: "60%" },
      ],
    };
  },
  computed: {
    totalSlides() {
      return 3;
    },
    isLastSlide() {
      return this.currentSlide === this.totalSlides - 1;
    },
    slides() {
      const t = (k) => this.$t(`onboarding.mobileIntro.${k}`);
      return [
        {
          title: t("personalizeTitle"),
          body: t("personalizeBody"),
          badge1: t("personalizeBadge1"),
          badge2: t("personalizeBadge2"),
          badge3: t("personalizeBadge3"),
        },
        {
          title: t("courseTitle"),
          body: t("courseBody"),
          badge1: t("courseBadge1"),
          badge2: t("courseBadge2"),
          badge3: t("courseBadge3"),
        },
        {
          title: t("habitTitle"),
          body: t("habitBody"),
          badge1: t("habitBadge1"),
          badge2: t("habitBadge2"),
          badge3: t("habitBadge3"),
        },
      ];
    },
    slide() {
      return this.slides[this.currentSlide];
    },
    personalizeBarsResolved() {
      return this.personalizeBars.map((b) => ({
        label: this.$t(b.labelKey),
        pct: b.pct,
      }));
    },
  },
  methods: {
    flagSrc(code) {
      return flagsStore.getFlag(code);
    },
    advanceSlide() {
      if (this.isLastSlide) return;
      this.slideDirection = "forward";
      this.currentSlide++;
    },
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },
    onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      const dy = e.changedTouches[0].clientY - this.touchStartY;
      // Ignore mostly-vertical swipes and small jitters.
      if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) <= 50) return;
      if (dx < 0 && this.currentSlide < this.totalSlides - 1) {
        this.slideDirection = "forward";
        this.currentSlide++;
      } else if (dx > 0 && this.currentSlide > 0) {
        this.slideDirection = "backward";
        this.currentSlide--;
      }
    },
    async signIn() {
      if (this.signingIn) return;
      this.signingIn = true;
      try {
        await authUtils.startAuth();
      } finally {
        this.signingIn = false;
      }
    },
  },
};
</script>

<style scoped>
.onboarding-screen {
  display: flex;
  flex-direction: column;
  height: var(--app-height, 100vh);
  background: #fff;
  user-select: none;
  overflow: hidden;
}

/* ---------- Hero (top zone) ---------- */
.hero {
  position: relative;
  flex: 1.15 1 0;
  min-height: 0;
  background: linear-gradient(170deg, #eef5fe 0%, #d3e6fb 100%);
  overflow: hidden;
}

.hero-decor {
  position: absolute;
  inset: 0;
}

.bubble {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
}

.bubble--1 {
  width: 220px;
  height: 220px;
  top: -60px;
  right: -40px;
  background: rgba(255, 255, 255, 0.5);
}

.bubble--2 {
  width: 180px;
  height: 180px;
  bottom: 10px;
  left: -60px;
  background: rgba(255, 255, 255, 0.35);
}

.bubble--3 {
  width: 120px;
  height: 120px;
  top: 40%;
  left: 30%;
  background: rgba(255, 255, 255, 0.25);
}

.hero-stage {
  position: absolute;
  /* Inset the content (badges + card) below the status bar / notch while the
     gradient still bleeds to the very top edge. Android WebView often reports
     env(safe-area-inset-top) as 0 even with viewport-fit=cover, so the 44px
     floor guarantees the top floating badges clear the status bar + camera
     cutout; notched devices that do report an inset use the larger value. */
  inset: max(env(safe-area-inset-top, 0px), 44px) 0 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ---------- Floating badges ---------- */
.badge {
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: #fff;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--al-black);
  box-shadow: 0 8px 22px rgba(45, 95, 160, 0.16);
  white-space: nowrap;
}

.badge--tl {
  top: 11%;
  left: 7%;
}

.badge--tr {
  top: 4%;
  right: 6%;
}

.badge--br {
  bottom: 9%;
  right: 8%;
}

.badge-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.badge-dot--purple {
  background: var(--al-ink-600);
}

.badge-dot--green {
  background: var(--al-success);
}

.badge-dot--orange {
  background: var(--al-orange);
}

/* ---------- Feature card (per-slide illustration) ---------- */
.feature-card {
  width: 74%;
  max-width: 320px;
  padding: 28px 26px;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 18px 44px rgba(40, 90, 160, 0.18);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Slide 0 — avatar + meters */
.avatar {
  width: 76px;
  height: 76px;
  margin: 4px auto 6px;
  border-radius: 50%;
  background: var(--al-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.meter {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.meter-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--al-gray);
}

.meter-track {
  height: 9px;
  border-radius: 999px;
  background: var(--al-gray-200);
  overflow: hidden;
}

.meter-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--al-primary);
}

/* Slide 1 — course rows + ring */
.course-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.course-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--al-black);
}

.course-ring {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ring-bg {
  fill: none;
  stroke: var(--al-gray-200);
  stroke-width: 3;
}

.ring-fg {
  fill: none;
  stroke: var(--al-primary);
  stroke-width: 3;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: center;
}

.course-ring-label {
  position: absolute;
  font-size: 0.6rem;
  font-weight: 700;
  color: var(--al-primary);
}

.lesson-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lesson-mark {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--al-gray-200);
  color: #fff;
}

.lesson-row--done .lesson-mark {
  background: var(--al-success);
}

.lesson-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--al-gray-300);
}

.lesson-bar {
  height: 10px;
  border-radius: 999px;
  background: var(--al-gray-200);
}

.lesson-row--done .lesson-bar {
  background: var(--al-light-blue);
}

/* Slide 2 — daily focal + flags */
.habit-focal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 0 2px;
}

.habit-num {
  font-size: 3.4rem;
  font-weight: 800;
  line-height: 1;
  color: var(--al-primary);
}

.habit-unit {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--al-gray);
}

.flag-strip {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 9px;
}

.flag-chip {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

/* ---------- Content (bottom zone) ---------- */
.content {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  padding: 26px 28px max(28px, env(safe-area-inset-bottom, 0px));
  gap: 18px;
}

.progress-dots {
  display: flex;
  gap: 7px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--al-gray-200);
  transition: all 0.25s ease;
}

.dot.active {
  width: 26px;
  background: var(--al-primary);
}

.slide-text {
  min-height: 132px;
}

.headline {
  font-size: 2.1rem;
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.01em;
  color: var(--al-black);
  margin-bottom: 12px;
  text-wrap: balance;
}

.body {
  font-size: 1.12rem;
  line-height: 1.45;
  color: var(--al-gray-700);
}

.cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.btn-primary {
  width: 100%;
  padding: 17px;
  border: none;
  border-radius: 16px;
  background: var(--al-primary);
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(45, 144, 235, 0.34);
  transition: background 0.15s ease, transform 0.1s ease;
}

.btn-primary:active:not(:disabled) {
  background: var(--al-primary-hover);
  transform: translateY(1px);
}

.btn-primary--disabled,
.btn-primary:disabled {
  background: var(--al-gray-300);
  box-shadow: none;
  cursor: not-allowed;
}

.signin-link {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 1rem;
  color: var(--al-gray);
  cursor: pointer;
}

.signin-link:active {
  color: var(--al-primary);
}

.signin-link--placeholder {
  visibility: hidden;
  pointer-events: none;
}

/* ---------- Slide transition ---------- */
.anim--forward {
  animation: slideInForward 0.26s ease;
}

.anim--backward {
  animation: slideInBackward 0.26s ease;
}

@keyframes slideInForward {
  from {
    opacity: 0;
    transform: translateX(22px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInBackward {
  from {
    opacity: 0;
    transform: translateX(-22px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .anim--forward,
  .anim--backward {
    animation: none;
  }
  .dot {
    transition: none;
  }
}

/* Short screens (e.g. iPhone SE, 667pt): compress the hero so the feature
   card never clips against the fixed-height content zone. */
@media (max-height: 720px) {
  .feature-card {
    padding: 20px 22px;
    gap: 12px;
    border-radius: 18px;
  }
  .avatar {
    width: 58px;
    height: 58px;
    margin: 0 auto 4px;
  }
  .avatar svg {
    width: 28px;
    height: 28px;
  }
  .habit-num {
    font-size: 2.7rem;
  }
  .flag-chip {
    width: 26px;
    height: 26px;
  }
  .content {
    padding: 16px 24px max(18px, env(safe-area-inset-bottom, 0px));
    gap: 13px;
  }
  .slide-text {
    min-height: 104px;
  }
  .headline {
    font-size: 1.8rem;
    margin-bottom: 8px;
  }
  .body {
    font-size: 1.02rem;
  }
  .btn-primary {
    padding: 15px;
  }
}
</style>
